/* transitions — one place that decides whether a work order may move forward.

   Every entry point that advances a job (the stage bar, the Schedule button,
   the calendar, the customer's schedule request, the primary action) asks this
   module first, so a shortcut cannot reach a state the dedicated path would
   have blocked. Before this existed, `schedule()` checked the install date and
   the permit hold while the stage bar only checked the change-order hold — so
   a job could read "Scheduled" with no date, past a permit hold that the
   owner had deliberately switched on.

   Two kinds of prerequisite:

     hard     nothing to confirm; the action is impossible as asked (there is
              no install date to schedule to).
     confirm  the office may overrule once, and the override is stamped on the
              job with who and why. Permits get pulled by phone and waived by
              inspectors; a gate nobody can overrule gets worked around
              instead of used. What matters is that it leaves a trace.

   blockersFor() is the same list without the dialogs, so a button can say what
   is missing BEFORE it is pressed rather than failing after. */
import { confirmDialog } from "../components";
import { permitBlocks, permitBlockMessage } from "./permits";
import { coHold } from "./changeorders";
import { permitDone } from "./readiness";
import { locatesRequired, permitRequired, verticalOf, jobTypesOf } from "./verticals";   // a concrete job digs no post hole: no 811 ticket, no permit (plan §5.4)
import { concreteReadinessFor, concreteStartGate } from "./concrete.steps";   // …its own checks instead: the slab before drilling, the moisture test before a coating

export type WoTarget = "scheduled" | "in_progress" | "completed";

export type Blocker = {
  key: "date" | "permit" | "changeorder" | "readiness";
  /* hard     the action is impossible as asked
     confirm  overridable once, stamped on the job
     warn     said out loud at the point of action, but not in the way — for
              things legitimately still outstanding at that moment */
  kind: "hard" | "confirm" | "warn";
  /** one short line for the pre-click hint under the button */
  label: string;
  title: string;
  body: string;
  confirmLabel: string;
};

const money0 = (n: any) => "$" + Math.round(Math.abs(Number(n) || 0)).toLocaleString("en-US");
const listOf = (a: string[]) => a.length <= 1 ? (a[0] || "") : a.slice(0, -1).join(", ") + " and " + a[a.length - 1];
/** whole days from today to a yyyy-mm-dd date, or null */
function daysUntil(ymd: any): number | null {
  if (!ymd) return null;
  const t = new Date(String(ymd) + "T00:00:00").getTime();
  if (isNaN(t)) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((t - today.getTime()) / 86400000);
}

/** what stands between this job and `to`, without asking anything */
export function blockersFor(wo: any, store: any, to: WoTarget): Blocker[] {
  const out: Blocker[] = [];
  if (!wo) return out;

  // an install date is a prerequisite of being scheduled, not a preference
  if (to === "scheduled" && !wo.scheduledStart) {
    out.push({
      key: "date", kind: "hard",
      label: "needs an install date",
      title: "Pick an install date first",
      body: "A job can't be scheduled without a date — set one on the job and try again.",
      confirmLabel: "OK",
    });
  }

  // the permit hold guards digging, so it guards starting as much as scheduling
  if (to === "scheduled" || to === "in_progress") {
    const block = permitBlocks(wo);
    if (block) {
      out.push({
        key: "permit", kind: "confirm",
        label: block.reason === "submitted" ? "permit not approved yet" : "no permit filed",
        title: "Permit isn't approved yet",
        body: permitBlockMessage(block),
        confirmLabel: (to === "scheduled" ? "Schedule" : "Start") + " anyway",
      });
    }
  }

  /* Site readiness. Scheduling deliberately does NOT wait for it: an 811 locate
     takes about three business days, so the crew is booked AROUND it, not after
     it. What scheduling does is say what is outstanding and how long there is
     to do it. Starting work is the moment that matters — that is when someone
     digs — so unmarked locates gate Start with an audited override, exactly
     like the permit hold. */
  {
    const r = (wo && wo.readiness) || {};
    const open: string[] = [];
    if (!r.siteVisit) open.push("site visit");
    if (permitRequired(wo) && !permitDone(wo)) open.push("permit");
    if (locatesRequired(wo) && !r.diggers) open.push("utility locates");
    const concrete = verticalOf(wo) === "concrete";
    const cxJobs = concrete ? jobTypesOf((wo.quoteId && (((store && store.quotes) || []) as any[]).find((x: any) => x && x.id === wo.quoteId)) || wo) : [];
    if (concrete) concreteReadinessFor(cxJobs).forEach((it) => { if (!r[it.key]) open.push(it.label.toLowerCase()); });
    ((r.custom || []) as any[]).forEach((c: any) => { if (c && !c.done) open.push(String(c.label || "").toLowerCase()); });

    if (open.length && to === "scheduled") {
      const days = daysUntil(wo.scheduledStart);
      const tight = locatesRequired(wo) && !r.diggers && days != null && days < 3;
      out.push({
        key: "readiness", kind: "warn",
        label: `${open.length} readiness item${open.length === 1 ? "" : "s"} outstanding`,
        title: "Book it — these are still open",
        body: `Still to do before the crew rolls: ${listOf(open)}.`
          + (tight
            ? `\n\nThe start date is ${days === 0 ? "today" : days === 1 ? "tomorrow" : "in " + days + " days"} and the locates aren't marked. An 811 ticket usually takes about three business days — call it now or move the date.`
            : locatesRequired(wo)
              ? "\n\nNone of this stops you booking the crew: an 811 ticket takes about three business days, so the date normally goes in first. You will be stopped from starting work until the locates are marked."
              : "\n\nNone of this stops you booking the crew."),
        confirmLabel: "Schedule it",
      });
    }
    // a concrete job's equivalent of unmarked locates: a lift with the slab
    // unchecked, a coating with no moisture test (lib/concrete.steps.ts)
    const cxGate = concrete && to === "in_progress" ? concreteStartGate(cxJobs, r) : null;
    if (cxGate) {
      out.push({ key: "readiness", kind: "confirm", label: cxGate.label, title: cxGate.title, body: cxGate.body, confirmLabel: "Start anyway" });
    }
    if (locatesRequired(wo) && !r.diggers && to === "in_progress") {
      out.push({
        key: "readiness", kind: "confirm",
        label: "utility locates not marked",
        title: "Locates aren't marked",
        body: "Nobody has confirmed the utility marks are on the ground, and starting work means digging. If the marks are there, tick \u201cUtilities located & marked\u201d on the job rather than overriding this — the override goes on the record with your name.",
        confirmLabel: "Start anyway",
      });
    }
  }

  // a pending change order means the scope and price aren't settled
  if (to === "scheduled" || to === "in_progress") {
    const h = coHold(wo, store);
    if (h.active) {
      const who = (h.q && h.q.customer) || wo.customer || "the customer";
      const list = h.pending.map((c: any) => `${c.label} (${(Number(c.amount) || 0) >= 0 ? "+" : "−"}${money0(c.amount)})`).join("; ");
      out.push({
        key: "changeorder", kind: "confirm",
        label: "change order awaiting approval",
        title: `Change order awaiting ${who}'s approval`,
        body: `${list}. The price and scope aren't settled until ${who} signs it on the portal. This is recorded on the job.`,
        confirmLabel: (to === "scheduled" ? "Schedule" : "Start") + " anyway",
      });
    }
  }
  return out;
}

/** the actor recorded against an override */
function actorOf(store: any) {
  return (store && store.settings && store.settings.memberName) || (store && store.user && store.user.email) || "the owner";
}

/* Ask for everything `to` requires. Returns null when the move is refused (a
   hard blocker, or a confirmation declined) — the caller does nothing in that
   case. Otherwise returns the override stamps to merge into the status patch,
   which is often `{}`. Overrides are written here and logged here so no caller
   can move a job past a hold without leaving the trace. */
export async function gateTransition(wo: any, store: any, to: WoTarget, opts?: { onHard?: (b: Blocker) => void }): Promise<any | null> {
  const blockers = blockersFor(wo, store, to);
  const hard = blockers.find((b) => b.kind === "hard");
  if (hard) {
    // a missing date is answered by pointing at the field, not by a dialog that
    // tells you to go and find it
    if (opts && opts.onHard) opts.onHard(hard);
    return null;
  }
  const extra: any = {};
  for (const b of blockers) {
    const ok = await confirmDialog({ title: b.title, body: b.body, confirmLabel: b.confirmLabel, cancelLabel: "Not yet", danger: b.kind !== "warn" });
    if (!ok) return null;
    // a warning is said, not stamped — there is nothing to override
    if (b.kind === "warn") continue;
    const who = actorOf(store);
    if (b.key === "permit") {
      const block = permitBlocks(wo);
      extra.permitOverride = { at: Date.now(), by: who, reason: block ? block.reason : "unknown" };
      if (store && store.addActivity) store.addActivity(`${b.confirmLabel.replace(" anyway", "d")} ${wo.customer || "a job"} without an approved permit — override by ${who}`);
    } else if (b.key === "readiness") {
      extra.readinessOverride = { at: Date.now(), by: who, action: b.confirmLabel };
      if (store && store.addActivity) store.addActivity(`${wo.customer || "A job"} started without the utility locates marked — override by ${who}`);
    } else if (b.key === "changeorder") {
      const h = coHold(wo, store);
      extra.coHoldOverride = { at: Date.now(), by: who, ids: h.ids, action: b.confirmLabel };
      if (store && store.addActivity) store.addActivity(`${b.confirmLabel} — ${wo.customer || wo.title || "job"} moved on with a change order still awaiting approval`);
    }
  }
  return extra;
}

/* What completing this job actually means, gathered so the person clicking can
   see it: what is still unticked, what the customer still owes, and which
   messages the completion will send. Completion fires review requests, photo
   posts and the balance chase — "Completed" is not a display filter. */
export function completionReview(wo: any, store: any) {
  const r = (wo && wo.readiness) || {};
  const open: string[] = [];
  if (!r.siteVisit) open.push("Site visit confirmed");
  if (permitRequired(wo) && !r.permit) open.push("Permit pulled");
  if (locatesRequired(wo) && !r.diggers) open.push("Utilities located & marked");
  if (verticalOf(wo) === "concrete") {
    const cq = wo.quoteId ? (((store && store.quotes) || []) as any[]).find((x: any) => x && x.id === wo.quoteId) : null;
    concreteReadinessFor(jobTypesOf(cq || wo)).forEach((it) => { if (!r[it.key]) open.push(it.label); });
  }
  (r.custom || []).forEach((c: any) => { if (c && !c.done) open.push(c.label); });

  const q = wo && wo.quoteId ? ((store && store.quotes) || []).find((x: any) => x && x.id === wo.quoteId) : null;
  const total = Number((q && q.total) || wo.total) || 0;
  const paid = q ? (Number(q.depositPaid) || 0) + ((q.payments || []).reduce((s: number, p: any) => s + (Number(p.amount) || 0), 0)) : 0;
  const balance = Math.round(Math.max(0, total - paid) * 100) / 100;

  // which completion messages go out for this business: the sequences are an
  // ARRAY (settings.sequences — the app's defaults until customized; the
  // review and referral ones fire on job_completed). This used to read them
  // as an object, so nothing was ever listed.
  const own = store && store.settings && store.settings.sequences;
  const dflt = (window as any).VFP && (window as any).VFP.DEFAULT_SETTINGS && (window as any).VFP.DEFAULT_SETTINGS.sequences;
  const seqs: any[] = Array.isArray(own) && own.length ? own : (Array.isArray(dflt) ? dflt : []);
  const seqOn = (id: string) => seqs.some((x: any) => x && x.id === id && x.on !== false && (x.trigger || "job_completed") === "job_completed");
  const willSend: string[] = [];
  if (seqOn("seq-review")) willSend.push("the review request");
  if (seqOn("seq-referral")) willSend.push("the referral ask");
  const photos = ((wo && wo.photos) || []).filter((p: any) => p && p.url).length;

  return { open, balance, total, paid, willSend, photos };
}

/** the completion confirmation — returns false when the user backs out */
export async function confirmCompletion(wo: any, store: any): Promise<boolean> {
  const rv = completionReview(wo, store);
  const lines: string[] = [];
  if (rv.open.length) lines.push(`Still unticked: ${rv.open.join(", ")}.`);
  if (rv.balance > 0) lines.push(`${wo.customer || "The customer"} still owes ${money0(rv.balance)} of ${money0(rv.total)}. Completing the job does not collect it — the pay link goes out in the wrap-up text, and the balance falls due tomorrow.`);
  else if (rv.total > 0) lines.push(`Paid in full — ${money0(rv.total)}.`);
  if (rv.willSend.length) lines.push(`Completing sends ${rv.willSend.join(" and ")}.`);
  lines.push("The warranty clock starts now.");
  return !!(await confirmDialog({
    title: `Mark ${wo.customer || "this job"} complete?`,
    body: lines.join("\n"),
    confirmLabel: "Mark complete",
    cancelLabel: "Not yet",
    danger: rv.open.length > 0 || rv.balance > 0,
  }));
}
